import en from './locale/en';

export const initialMessages = { en };

// Keep this list aligned with index.js. Other locale directories are not enabled.
const localeLoaders = {
  ar: () => import('./locale/ar'),
  bg: () => import('./locale/bg'),
  ca: () => import('./locale/ca'),
  cs: () => import('./locale/cs'),
  da: () => import('./locale/da'),
  de: () => import('./locale/de'),
  el: () => import('./locale/el'),
  en: () => Promise.resolve({ default: en }),
  es: () => import('./locale/es'),
  et: () => import('./locale/et'),
  fa: () => import('./locale/fa'),
  fi: () => import('./locale/fi'),
  fr: () => import('./locale/fr'),
  he: () => import('./locale/he'),
  hi: () => import('./locale/hi'),
  hu: () => import('./locale/hu'),
  id: () => import('./locale/id'),
  it: () => import('./locale/it'),
  ja: () => import('./locale/ja'),
  ko: () => import('./locale/ko'),
  ml: () => import('./locale/ml'),
  lv: () => import('./locale/lv'),
  nl: () => import('./locale/nl'),
  no: () => import('./locale/no'),
  pl: () => import('./locale/pl'),
  pt_BR: () => import('./locale/pt_BR'),
  pt: () => import('./locale/pt'),
  ro: () => import('./locale/ro'),
  ru: () => import('./locale/ru'),
  sk: () => import('./locale/sk'),
  sl: () => import('./locale/sl'),
  sr: () => import('./locale/sr'),
  sv: () => import('./locale/sv'),
  ta: () => import('./locale/ta'),
  th: () => import('./locale/th'),
  tr: () => import('./locale/tr'),
  uk: () => import('./locale/uk'),
  uz: () => import('./locale/uz'),
  vi: () => import('./locale/vi'),
  zh_CN: () => import('./locale/zh_CN'),
  zh_TW: () => import('./locale/zh_TW'),
  is: () => import('./locale/is'),
  lt: () => import('./locale/lt'),
};

export const supportedLocales = Object.freeze(Object.keys(localeLoaders));
const localePromises = new Map();
const latestRequests = new WeakMap();

export const loadLocaleMessages = locale => {
  if (!Object.prototype.hasOwnProperty.call(localeLoaders, locale)) {
    return Promise.reject(new Error(`Unsupported locale: ${locale}`));
  }

  if (!localePromises.has(locale)) {
    const pending = localeLoaders[locale]()
      .then(module => module.default)
      .catch(error => {
        localePromises.delete(locale);
        throw error;
      });
    localePromises.set(locale, pending);
  }

  return localePromises.get(locale);
};

export const setLocale = async (composer, locale, isCurrent = () => true) => {
  if (!locale) return false;

  const request = Symbol(locale);
  latestRequests.set(composer, request);
  const messages = await loadLocaleMessages(locale);
  // Even a cached import yields, so the caller may have left its account.
  if (latestRequests.get(composer) !== request || !isCurrent()) return false;

  if (!composer.availableLocales.includes(locale)) {
    composer.setLocaleMessage(locale, messages);
  }
  composer.locale.value = locale;
  return true;
};

export const reportLocaleError = error => {
  // eslint-disable-next-line no-console
  console.error('Unable to load the interface language', error);
};

export const mountWithLocale = (app, composer, getLocale) => {
  const mount = async () => {
    try {
      await setLocale(composer, getLocale() || 'en');
    } catch (error) {
      // A failed locale download must not prevent login or account startup.
      reportLocaleError(error);
    }
    app.mount('#app');
  };

  if (document.readyState === 'complete') return mount();
  window.addEventListener('load', mount, { once: true });
  return undefined;
};
